// Pure helpers for the Weekly Report tab and its Word export (#576, #579).
//
// A module of its own so the labels, grouping and filenames can be
// tested without rendering the tab (which pulls in apiClient → MSAL).

import type {
  InternalWeeklyReport, WeeklyReportBase, WeeklyReportCarryForward, WeeklyReportCarryPerson, WeeklyReportDay, WeeklyReportEquipment,
  WeeklyReportExtraSection, WeeklyReportLog, WeeklyReportNarrative, WeeklyReportPerson, WeeklyReportSectionKey,
} from '@/lib/apiClient';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Parsed component-wise, so no timezone can move the day (a UTC parse lands the evening before in Eastern). */
function parts(ymd: string): { y: number; m: number; d: number; dow: number } {
  const [y, m, d] = ymd.split('-').map(Number);
  return { y, m, d, dow: new Date(y, m - 1, d).getDay() };
}

export function addYmd(ymd: string, n: number): string {
  const { y, m, d } = parts(ymd);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/** "September 27" */
export function monthDay(ymd: string): string {
  const { m, d } = parts(ymd);
  return `${MONTHS[m - 1]} ${d}`;
}

/** "September 27, 2026" */
export function longDate(ymd: string): string {
  return `${monthDay(ymd)}, ${parts(ymd).y}`;
}

/** "Monday, September 28, 2026" */
export function dayHeading(ymd: string): string {
  return `${WEEKDAYS[parts(ymd).dow]}, ${longDate(ymd)}`;
}

/**
 * "Week of September 27".
 *
 * Weeks start on SUNDAY, the crew schedule's convention, because the
 * report's manpower rows are crew-schedule weeks. The label says "Week of"
 * plus the Sunday, never a bare date, for the reason #452 records: a bare
 * date next to a crew figure reads as the day the crew starts.
 */
export function weekOf(weekStart: string): string {
  return `Week of ${monthDay(weekStart)}`;
}

/** "the week of September 27", inside a sentence. */
export function theWeekOf(weekStart: string): string {
  return `the week of ${monthDay(weekStart)}`;
}

/**
 * The warning before a job's FIRST internal upload or "No report" mark, when
 * it is for an old week; null when there is nothing to warn about.
 *
 * ⚠️ The first one starts the job's chain at its week, and nothing can be
 * undone. Started weeks back (say, on a week opened while browsing the strip),
 * every week since owes its report or a mark, in order, before this week's can
 * go up. Last week or this week piles nothing up, so those pass quietly.
 */
export function chainStartWarning(weekStart: string, thisWeek: string, chainStart: string | null): string | null {
  if (chainStart !== null || weekStart >= thisWeek) return null;
  let owed = 0;
  for (let w = addYmd(weekStart, 7); w <= thisWeek; w = addYmd(w, 7)) owed++;
  if (owed < 2) return null;
  return `This is the job’s first finalized report, so its reports will start with ${theWeekOf(weekStart)}. `
    + `Each of the ${owed} weeks after it, through this week, will then need its internal report or a “No report” mark, `
    + 'in order, before this week’s can be uploaded. This can’t be undone.';
}

/** "Sep 27 – Oct 3, 2026", the week bar's label. */
export function rangeLabel(from: string, to: string): string {
  const short = (ymd: string) => `${MONTHS[parts(ymd).m - 1].slice(0, 3)} ${parts(ymd).d}`;
  return `${short(from)} – ${short(to)}, ${parts(to).y}`;
}

/** The report's generation time in Eastern, the template's "September 28, 2026 – 2:30PM". */
export function generatedLabel(iso: string): string {
  const dt = new Date(iso);
  const date = dt.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'long', day: 'numeric', year: 'numeric' });
  // Any whitespace, not just ' ': newer ICU puts a narrow no-break space
  // (U+202F) before AM/PM.
  const time = dt.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }).replace(/\s+/g, '');
  return `${date} – ${time}`;
}

/** "Sunny, 70° / 50°, rain 0.2 in, wind 5 mph", from whatever was recorded. */
export function weatherLine(w: WeeklyReportLog['weather']): string {
  const temps = w.hi != null || w.lo != null ? `${w.hi ?? '—'}° / ${w.lo ?? '—'}°` : null;
  return [w.cond, temps, w.precip ? `precip ${w.precip}` : null, w.wind ? `wind ${w.wind}` : null]
    .filter((s): s is string => !!s && s.trim() !== '').join(', ');
}

export function crewTotals(log: WeeklyReportLog): { workers: number; manhours: number } {
  return log.crews.reduce((t, c) => ({
    workers: t.workers + (c.workers ?? 0),
    manhours: t.manhours + (c.workers ?? 0) * (c.hours ?? 0),
  }), { workers: 0, manhours: 0 });
}

/** "130 CY Concrete" */
export function quantityLine(q: WeeklyReportLog['quantities'][number]): string {
  return [q.qty != null ? String(q.qty) : null, q.unit, q.item].filter((s): s is string => !!s && s.trim() !== '').join(' ');
}

/**
 * Whether a day contributes anything to an EXPORT. A draft log is not a
 * record yet, so it is left out of both exports; the day's photos still count.
 */
export function exportableLog(day: WeeklyReportDay): WeeklyReportLog | null {
  return day.log && day.log.status === 'submitted' ? day.log : null;
}

export function dayHasExportContent(day: WeeklyReportDay): boolean {
  return exportableLog(day) !== null || day.photoPosts.some((p) => p.photos.length > 0);
}

export function photoCount(day: WeeklyReportDay): number {
  return day.photoPosts.reduce((n, p) => n + p.photos.length, 0);
}

/** The lookback days grouped by week (one week today), oldest first, for the tab, the export and the client-export window. */
export function daysByWeek(report: WeeklyReportBase): { weekStart: string; days: WeeklyReportDay[] }[] {
  return report.window.lookback.weekStarts.map((ws) => {
    const we = addYmd(ws, 6);
    return { weekStart: ws, days: report.days.filter((d) => d.date >= ws && d.date <= we) };
  });
}

/**
 * The template's filename shape, with the week named:
 * "<job> - <title> - Weekly Project Report - WeekOf MM-DD-YY.docx", and
 * "INTERNAL" before "Weekly" on the internal copy. "WeekOf" plus the report
 * week's Sunday, the same date every "Week of" label in the app shows (#576).
 */
export function reportFilename(report: WeeklyReportBase & { version: 'internal' | 'client' }): string {
  const { y, m, d } = parts(report.window.weekStart);
  const stamp = `${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}-${String(y).slice(-2)}`;
  const job = [report.project.jobNumber, report.project.title].filter((s) => s && s.trim()).join(' - ');
  const kind = report.version === 'internal' ? 'INTERNAL Weekly Project Report' : 'Weekly Project Report';
  const raw = [job || 'Project', kind, `WeekOf ${stamp}`].join(' - ');
  // Windows refuses these in a filename; a job title can contain them.
  return `${raw.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim()}.docx`;
}

/**
 * The note on a Manpower row, shared by the tab and the Word export, or null
 * when the figure needs none.
 *
 * Only last week's ACTUAL row can need one: unknown when the week hasn't been
 * pulled or payroll couldn't be read, and PARTIAL when only some of it has, so
 * the busiest day so far isn't read as the week's.
 *
 * Whether the week is pulled comes from the server (`manpower.lastWeek`),
 * which reads the payroll sync's own record of what it pulled. Not the newest
 * work date: an empty Saturday and an unpulled one look the same there, and
 * Saturdays do carry hours (Codex on #591).
 */
export function manpowerNote(report: InternalWeeklyReport, row: InternalWeeklyReport['manpower']['rows'][number]): string | null {
  if (row.basis !== 'actual') return null;
  const m = report.manpower;
  if (row.headcount == null) {
    // Payroll is matched by job number, so without one it can never arrive;
    // "not yet synced" would read as payroll running late.
    if (!report.project.jobNumber?.trim()) return 'No job number yet, so no payroll can be matched';
    if (!m.payrollAvailable) return 'Payroll could not be read';
    return 'Payroll not yet synced for this week';
  }
  if (m.lastWeek.status === 'partial') {
    return m.lastWeek.coveredThrough
      ? `Partial week: payroll synced through ${longDate(m.lastWeek.coveredThrough)}`
      : 'Partial week: payroll not yet synced for every day';
  }
  return null;
}

/** How far the week strip reaches either side of this week. */
export const STRIP_WEEKS_BACK = 52;
export const STRIP_WEEKS_AHEAD = 12;

/**
 * The Sundays the week strip offers, oldest first: a year back to twelve
 * weeks ahead of this week.
 *
 * When a link names a week outside that span, the same-sized span is taken
 * around THAT week instead, so the selection is always on the strip without
 * the strip growing to cover the whole gap: a link to 2099 would otherwise
 * render thousands of cells. "Back to this week" is the way home from there.
 */
export function stripWeeks(thisWeek: string, selected: string): string[] {
  const span = (anchor: string): [string, string] => [addYmd(anchor, -7 * STRIP_WEEKS_BACK), addYmd(anchor, 7 * STRIP_WEEKS_AHEAD)];
  const [homeFirst, homeLast] = span(thisWeek);
  const [first, last] = selected >= homeFirst && selected <= homeLast ? [homeFirst, homeLast] : span(selected);
  const out: string[] = [];
  for (let w = first; w <= last; w = addYmd(w, 7)) out.push(w);
  return out;
}

/** "Sep 27", the strip cell's date. */
export function shortMonthDay(ymd: string): string {
  return `${MONTHS[parts(ymd).m - 1].slice(0, 3)} ${parts(ymd).d}`;
}

// ─── Carry-forward: last finalized report's typed fields ─────────────────────

export type TeamRow = WeeklyReportPerson & {
  /** The whole row is from the previous finalized report, not from the job. */
  carried: boolean;
  /** On a row from the job: the phone or email the job lacks, filled from the previous report for the same person. */
  carriedContact?: ('phone' | 'email')[];
};

const roleKey = (role: string) => role.replace(/\s+/g, ' ').trim().toLowerCase();

/** Short forms of the job's roles a PM types instead. */
const ROLE_ALIASES: Record<string, string> = {
  pm: 'project manager', ppm: 'project manager', pe: 'project engineer',
  super: 'superintendent', supt: 'superintendent', est: 'estimator',
};
/** Words that rank a role without changing who holds it ("Senior Project Manager" is the PM). */
const RANKS = new Set(['senior', 'sr', 'lead', 'chief', 'principal']);

/**
 * The plain role(s) a label names, for comparing with the job's: lower-case,
 * its note dropped ("(PM)", ": nights", ", assistant", " - nights"), split
 * where it names two ("Project Manager/PE"), a rank word in front and a grade
 * at the end dropped ("Sr.", "II"), a plural made singular, a short form
 * spelled out. "Assistant Project Manager" stays itself: an assistant is
 * someone else.
 */
function roleCores(role: string): string[] {
  const label = role.toLowerCase().replace(/\([^)]*\)/g, ' ').split(/\s[-–—]\s|[:,;]/)[0];
  return label.split(/[/&+]|\band\b/).map((part) => {
    const words = part.replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);
    while (words.length > 1 && RANKS.has(words[0])) words.shift();
    while (words.length > 1 && /^(i{1,3}|iv|\d+)$/.test(words[words.length - 1])) words.pop();
    if (words.length) words[words.length - 1] = words[words.length - 1].replace(/([^s])s$/, '$1');
    const core = words.join(' ');
    return ROLE_ALIASES[core] ?? core;
  }).filter(Boolean);
}

/** The roles the job fills (`fromJob`), for isJobRole. */
export function jobRoleKeys(team: readonly { role: string; fromJob: boolean }[]): Set<string> {
  return new Set(team.filter((t) => t.fromJob).flatMap((t) => roleCores(t.role)));
}

/**
 * Is `role` one of the job's roles, however the PM relabeled it in Word
 * ("Project Manager (PM)", "Sr. PM", "Project Managers", "Superintendent -
 * nights")? Otherwise a relabeled row slips past "the job wins" and puts back
 * a person the job has since taken off. A different role that merely contains
 * one is not it: "General Superintendent", "Assistant Project Manager",
 * "Safety Coordinator".
 *
 * A label naming two roles is the job's only when BOTH are ("Project
 * Manager/PE" is; "Safety & Health Manager" and "QA/QC & Safety" are someone
 * the PM added).
 */
export function isJobRole(role: string, jobRoles: ReadonlySet<string>): boolean {
  const cores = roleCores(role);
  return cores.length > 0 && cores.every((core) => jobRoles.has(core));
}

/**
 * The East & Westbrook team: the job's own assignments, with the template's
 * blank roles filled from the previous finalized report.
 *
 * ⚠️ THE JOB WINS, BY ROLE. A role the job fills (`fromJob`: Estimator, PM,
 * Engineer, Superintendent, Safety) is never filled from last week's file,
 * even when nobody holds it now: the file's name there is last week's
 * assignment, and carrying it would put back someone the job has since taken
 * off. Rows the PM ADDED in Word, for other roles (a second foreman, say),
 * come along, placed after their role.
 */
export function teamWithCarry(team: WeeklyReportPerson[], carry: WeeklyReportCarryForward | null): TeamRow[] {
  const pool = (carry?.team ?? []).filter((p) => p.name?.trim());
  const used = new Set<number>();
  const jobRoles = jobRoleKeys(team);
  const out: TeamRow[] = team.map((t) => {
    if (t.name && t.fromJob) return withTypedContact(t, carry);
    if (t.name || t.fromJob) return { ...t, carried: false };
    const i = pool.findIndex((p, j) => !used.has(j) && roleKey(p.role) === roleKey(t.role));
    if (i < 0) return { ...t, carried: false };
    used.add(i);
    return { role: t.role, name: pool[i].name, phone: pool[i].phone, email: pool[i].email, fromJob: false, carried: true };
  });
  pool.forEach((p, j) => {
    if (used.has(j) || isJobRole(p.role, jobRoles)) return;
    const row: TeamRow = { role: p.role || 'Team', name: p.name, phone: p.phone, email: p.email, fromJob: false, carried: true };
    const after = out.map((r) => roleKey(r.role)).lastIndexOf(roleKey(p.role));
    if (after >= 0) out.splice(after + 1, 0, row); else out.push(row);
  });
  return out;
}

/** The customer team as the previous finalized report had it, or null to start from the template's empty roles. */
export function customerTeamRows(carry: WeeklyReportCarryForward | null): WeeklyReportCarryPerson[] | null {
  const rows = carry?.customerTeam.filter((p) => p.role || p.name || p.phone || p.email) ?? [];
  // A table of empty roles is the template's, not anyone's contacts.
  return rows.some((p) => p.name || p.phone || p.email) ? rows : null;
}

/** One look-ahead week's lines from the previous report, matched by date, so the weeks shift along. */
export function lookaheadLines(carry: WeeklyReportCarryForward | null, weekStart: string): string[] {
  return carry?.lookahead.find((w) => w.weekStart === weekStart)?.lines ?? [];
}

/** The narrative sections whose text carries over whole. */
export type CarriedSection = WeeklyReportNarrative;

export function sectionLines(carry: WeeklyReportCarryForward | null, section: CarriedSection): string[] {
  return carry?.[section] ?? [];
}

/** Tables the PM put in a narrative section last week, after its lines. */
export function sectionTables(carry: WeeklyReportCarryForward | null, section: CarriedSection): WeeklyReportCarryForward['tables'] {
  return (carry?.tables ?? []).filter((t) => t.section === section);
}

/** Sections the PM added last week, that come right after `after` (null: before the first). */
export function extraSectionsAfter(carry: WeeklyReportCarryForward | null, after: WeeklyReportSectionKey | null): WeeklyReportExtraSection[] {
  return (carry?.extraSections ?? []).filter((s) => s.after === after);
}

/**
 * A row the JOB fills, with a phone or email it lacks filled from last week's
 * report, but only for the SAME PERSON: a number typed for someone since taken
 * off the job must not land on whoever holds the role now.
 */
function withTypedContact(t: WeeklyReportPerson, carry: WeeklyReportCarryForward | null): TeamRow {
  const name = roleKey(t.name ?? '');
  const typed = name ? (carry?.jobTeam ?? []).find((p) => roleKey(p.name ?? '') === name) : undefined;
  const phone = t.phone ?? typed?.phone ?? null;
  const email = t.email ?? typed?.email ?? null;
  const carriedContact = [
    ...(!t.phone && phone ? ['phone' as const] : []),
    ...(!t.email && email ? ['email' as const] : []),
  ];
  return { ...t, phone, email, carried: false, ...(carriedContact.length ? { carriedContact } : {}) };
}

// ─── Project Details ────────────────────────────────────────────────────────

/** The template's Project Details rows, as the Tracker fills them. */
export function trackerDetails(p: WeeklyReportBase['project']): { label: string; value: string }[] {
  return [
    { label: 'Project Name', value: p.title?.trim() ?? '' },
    { label: 'Project Number', value: p.jobNumber?.trim() ?? '' },
    { label: 'Project Address', value: p.address?.trim() ?? '' },
    { label: 'Mobilization Date', value: p.firstMobilization ? longDate(p.firstMobilization) : '' },
  ];
}

/** A label by its letters alone, so "Project address:" is "Project Address". */
export const labelKey = (label: string) => label.toUpperCase().replace(/[^A-Z0-9]/g, '');

export interface DetailRow { label: string; value: string; carried: boolean }

/**
 * Project Details: the Tracker's value wherever it has one; a field it has
 * blank, from what was typed in last week's report; and rows the PM added.
 */
export function detailRows(p: WeeklyReportBase['project'], carry: WeeklyReportCarryForward | null): DetailRow[] {
  const typed = carry?.details ?? [];
  const tracker = trackerDetails(p);
  const known = new Set(tracker.map((d) => labelKey(d.label)));
  const rows: DetailRow[] = tracker.flatMap(({ label, value }) => {
    const t = typed.find((d) => !d.added && labelKey(d.label) === labelKey(label));
    const own: DetailRow = value ? { label, value, carried: false } : t ? { label, value: t.value, carried: true } : { label, value: '', carried: false };
    // A further row the PM added with the same label (a second site's address), right after it.
    const more = typed.filter((d) => d.added && labelKey(d.label) === labelKey(label)).map((d) => ({ label: d.label, value: d.value, carried: true }));
    return [own, ...more];
  });
  for (const d of typed) if (!known.has(labelKey(d.label))) rows.push({ label: d.label, value: d.value, carried: true });
  return rows;
}

// ─── Manpower ───────────────────────────────────────────────────────────────

/**
 * The note under the Manpower table, in the tab and the export. The reader
 * knows it by its text too: a file exported before the export marked its own
 * lines (TRACKER_LINE_STYLE) has it unmarked, and it must not carry as typed.
 */
export const MANPOWER_FOOTNOTE = 'Headcount is the busiest day of the week: actual from payroll, scheduled and forecast from the crew schedule.';

/** "October 4 (scheduled)", a Manpower row's Week Of cell, in the tab and the export. */
export function manpowerLabel(row: InternalWeeklyReport['manpower']['rows'][number]): string {
  return `${monthDay(row.weekStart)} (${row.basis})`;
}

export interface ManpowerRowView {
  label: string;
  /** The count, '—' when there is none. */
  headcount: string;
  /** The Tracker's own note (payroll not synced, a partial week), or null. */
  trackerNote: string | null;
  /** What was typed in last week's report for the same week, or null. */
  typedNote: string | null;
  /** The count came from last week's report: the schedule has none for the week. */
  headcountCarried: boolean;
  /** A row the PM added last week (a later week, or one the Tracker can't date). */
  added: boolean;
}

/**
 * The Manpower table: the Tracker's counts, with last week's typed notes on
 * the SAME weeks (matched by date, so they move up a row with the weeks), and
 * a count typed for a scheduled or forecast week the schedule has nothing for.
 * ⚠️ "Nothing" is 0 there, not null: the API reports an unscheduled week as 0
 * crew (WeeklyReportService.manpower), so 0 is what a typed count fills.
 * Never a typed count on the ACTUAL row: unknown there means payroll hasn't
 * arrived, and last week's forecast must not stand in for it. Rows the PM
 * added come after; ones for a week now past drop off.
 */
export function manpowerRows(r: InternalWeeklyReport): ManpowerRowView[] {
  const typed = r.carryForward?.manpower ?? [];
  const live = r.manpower.rows;
  const last = live[live.length - 1]?.weekStart ?? '';
  // A row the PM added: flagged so by the reader, or (stored before the flag)
  // one the Tracker has no week for.
  const isAdded = (m: (typeof typed)[number]) => m.added || m.weekStart === null || m.weekStart > last;
  const addedRow = (m: (typeof typed)[number]): ManpowerRowView => ({
    label: m.label || (m.weekStart ? monthDay(m.weekStart) : ''),
    headcount: m.headcount ?? '',
    trackerNote: null,
    typedNote: m.notes,
    headcountCarried: !!m.headcount,
    added: true,
  });
  const rows: ManpowerRowView[] = live.flatMap((row) => {
    const t = typed.find((m) => !isAdded(m) && m.weekStart === row.weekStart);
    const unscheduled = row.basis !== 'actual' && !row.headcount;
    const fillCount = unscheduled && !!t?.headcount;
    const view: ManpowerRowView = {
      label: manpowerLabel(row),
      headcount: fillCount ? t!.headcount! : row.headcount != null ? String(row.headcount) : '—',
      trackerNote: manpowerNote(r, row),
      typedNote: t?.notes ?? null,
      headcountCarried: fillCount,
      added: false,
    };
    // Rows the PM added for this same week (a subcontractor breakout), right after it.
    return [view, ...typed.filter((m) => isAdded(m) && m.weekStart === row.weekStart).map(addedRow)];
  });
  // Then added rows for later weeks, or none; ones for a week now past drop off.
  for (const m of typed) {
    if (!isAdded(m) || (m.weekStart !== null && m.weekStart <= last)) continue;
    rows.push(addedRow(m));
  }
  return rows;
}

// ─── Equipment ──────────────────────────────────────────────────────────────

/**
 * One equipment item as the export writes it: "2 × Telehandler – called off,
 * on site since September 1, 2026". The reader matches these exactly, so a
 * line the PM left alone is the Tracker's, and anything else was typed.
 */
export function equipmentLine(e: WeeklyReportEquipment): string {
  const qty = e.quantity && e.quantity > 1 ? `${e.quantity} × ` : '';
  const since = e.onSiteFrom ? `on site since ${longDate(e.onSiteFrom)}` : null;
  const until = e.endDate ? `through ${longDate(e.endDate)}` : null;
  // By code, as the Equipment module decides 'on site'; labels have been renamed before.
  const status = e.status && e.statusCode !== 'on_site' ? e.status.toLowerCase() : null;
  return [`${qty}${e.name}`, [status, since, until].filter(Boolean).join(', ')].filter(Boolean).join(' – ');
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * For a file exported BEFORE the export marked its own lines
 * (TRACKER_LINE_STYLE): in a marked file the mark decides, and this isn't
 * asked. Is `line` the Tracker's own line for one of `items`, as the export
 * wrote it, even if the item's status, dates or count have changed since? Matched by
 * the item: its name, after any "2 × ", then nothing or the " – " the export
 * puts before the details. A line about something else ("Conex (owned)", or
 * "Telehandler returning Friday") isn't one. ⚠️ A note added after the
 * item's own dash ("Telehandler – returning Friday") reads as the item's line,
 * so it isn't carried: the details after that dash are the Tracker's, rebuilt
 * every week. A note about an item belongs on a line of its own.
 *
 * ⚠️ By the item, not the whole line: compared with today's line, a line
 * exported before an item was called off would read as typed, and then come
 * back every week as if the PM had written it.
 *
 * ⚠️ The "2 × " is an optional part of the pattern, not stripped from the line
 * first: an item whose name itself starts with digits and an x ("4x4 Gator")
 * would lose part of its name to a strip, whereas the pattern gives the
 * quantity up when taking it would leave the rest of the name unmatched. Where
 * the leading digits and x read either way (an item called "4 Gator", the line
 * "4x4 Gator"), the line counts as the item's.
 */
export function isTrackerEquipmentLine(line: string, items: readonly WeeklyReportEquipment[]): boolean {
  const written = line.replace(/\s+/g, ' ').trim();
  return items.some((e) => {
    const name = e.name.replace(/\s+/g, ' ').trim();
    return !!name && new RegExp(`^(?:\\d+\\s*[×x]\\s*)?${escapeRegExp(name)}\\s*(?:[–—-]\\s|$)`, 'i').test(written);
  });
}

/**
 * The Word paragraph style the export gives the lines it writes from the job's
 * own data that the reader must tell from typed ones: its equipment lines, and
 * every line of its photo grid (so a table the PM makes with a picture isn't
 * taken for it). The reader knows them whatever the job's list says by upload
 * time: an item
 * deleted from the Tracker after the export no longer matches anything, and
 * without the mark its line would come back every week as if typed (Codex on
 * #640). Its "next" style is Normal, so a line the PM adds with Enter isn't
 * marked; text typed INTO a marked line is the Tracker's, like a note after
 * its dash. The id is its name without spaces, which is what Word keeps.
 */
export const TRACKER_LINE_STYLE = 'TrackerLine';

/** Equipment lines the PM typed last week (owned items, a conex), after the Tracker's own. */
export function typedEquipment(carry: WeeklyReportCarryForward | null): string[] {
  return carry?.equipment ?? [];
}

/** Every photo URL a report's EXPORT would embed (drafts' photos included; see dayHasExportContent). */
export function exportPhotoUrls(report: WeeklyReportBase): string[] {
  return [...new Set(report.days.flatMap((d) => d.photoPosts.flatMap((p) => p.photos.map((ph) => ph.webUrl))))];
}
